import { AfterViewInit, Component, Input, OnDestroy, OnInit, OnChanges } from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { Counter } from 'orv-ui-base-lib';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-clustered-chart',
  templateUrl: './clustered-chart.component.html',
  styleUrls: ['./clustered-chart.component.scss']
})
export class ClusteredChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() id: string = '';
  @Input() datas: Counter[];
  @Input() field: string;
  @Input() height: number = 200;
  @Input() showLegend: boolean = true;
  @Input() orientation: string = 'vertical';
  @Input() formatterCategory: (value: string) => string;
  @Input() formatterValue: (value: string) => string;
  @Input() tooltipText: string;

  chart: any;

  viewInitialized = false;

  constructor(private translateService: TranslateService) {
  }

  ngOnInit() {
  }

  ngOnChanges() {
    if (this.datas && this.datas.length && this.viewInitialized) {
      this.initChart();
    }
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    if (this.datas && this.datas.length) {
      this.initChart();
    }
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  }

  initChart() {
    setTimeout(() => this.createChart());
  }

  createChart() {
    if (this.chart) {
      this.chart.dispose();
    } else {
      am4core.useTheme(am4themes_animated);
    }

    this.chart = am4core.create(this.id, am4charts.XYChart);

    if (this.showLegend) {
      this.chart.legend = new am4charts.Legend();
      this.chart.legend.position = 'top';
      this.chart.legend.paddingBottom = 20;
      this.chart.legend.labels.template.maxWidth = 95;
    }

    const categoryAxis = this.orientation === 'vertical' ?
      this.chart.xAxes.push(new am4charts.CategoryAxis()) : this.chart.yAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = this.field;
    categoryAxis.renderer.minGridDistance = 30;
    if (this.orientation === 'vertical') {
      categoryAxis.renderer.labels.template.horizontalCenter = 'right';
      categoryAxis.renderer.labels.template.verticalCenter = 'middle';
      categoryAxis.renderer.labels.template.rotation = 270;
    }
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.minHeight = 110;
    categoryAxis.renderer.labels.template.adapter.add('text', (label: string, target: any) => {
      if (this.formatterCategory) return this.formatterCategory(target.dataItem.category);
      return label;
    });

    const valueAxis = this.orientation === 'vertical' ?
      this.chart.yAxes.push(new am4charts.ValueAxis()) : this.chart.xAxes.push(new am4charts.ValueAxis());
    valueAxis.min = 0;
    valueAxis.renderer.labels.template.adapter.add('text', (label: string) => {
      if (this.formatterValue) return this.formatterValue(label);
      return label;
    });

    this.chart.cursor = new am4charts.XYCursor();

    if (this.orientation === 'vertical') {
      this.chart.scrollbarX = new am4core.Scrollbar();
    } else {
      this.chart.scrollbarY = new am4core.Scrollbar();
    }

    this.chart.data = this.datas;

    const keys = {};
    this.datas.forEach(d => {
      Object.keys(d).forEach(k => {
        keys[k] = true;
      });
    });

    Object.keys(keys).forEach(key => {
      if (key !== this.field) {
        this.createSeries(key, this.translateService.instant(`labels.${key}`));
      }
    });

  }

  private createSeries(value: string, name: string) {
    const series = this.chart.series.push(new am4charts.ColumnSeries());
    series.name = name;
    if (this.orientation === 'vertical') {
      series.dataFields.valueY = value;
      series.dataFields.categoryX = this.field;
      series.columns.template.tooltipText = this.tooltipText;
      series.tooltip.pointerOrientation = 'horizontal';
    } else {
      series.dataFields.valueX = value;
      series.dataFields.categoryY = this.field;
      series.columns.template.tooltipText = this.tooltipText;
      series.tooltip.pointerOrientation = 'vertical';
    }
    return series;
  }
}
